import type { BatchRequest, CallRequest, SignatureRequest, Step } from '../types.action-flow'

/**
 * Group contiguous call requests into batches, breaking the batch around any
 * `before` / `after` boundaries the author declared.
 *
 * Grouping rules:
 * - A call with `before` ends the prior batch and starts a new one.
 * - A call with `after` ends its own batch.
 *
 * Example — the wizard's deploy → configure shape:
 *
 * ```ts
 * groupCallRequests([
 *   { label: 'Deploy',    getCall, after: waitForConfirmations(2) },
 *   { label: 'Configure', getCall },
 * ])
 * // ⇒ [[Deploy], [Configure]]
 * ```
 *
 * Both batches are sent via `wallet_sendCalls` on EIP-5792 wallets — even
 * the single-call ones — so Safe / multisig flows keep their proposal path.
 * The `after` callback fires between the two batches, gating Configure's gas
 * estimation on Deploy reaching N confirmations.
 *
 * Same array with no callbacks would collapse to one batch:
 *
 * ```ts
 * groupCallRequests([
 *   { label: 'Deploy',    getCall },
 *   { label: 'Configure', getCall },
 * ])
 * // ⇒ [[Deploy, Configure]]
 * ```
 *
 * On non-EIP-5792 wallets each call is its own `singleTx` regardless of
 * grouping; the rule is a no-op there.
 */
function groupCallRequests(callRequests: readonly CallRequest[]): CallRequest[][] {
  if (callRequests.length === 0) return []
  const groups: CallRequest[][] = []
  let current: CallRequest[] = []
  for (const req of callRequests) {
    if (req.before && current.length > 0) {
      groups.push(current)
      current = []
    }
    current.push(req)
    if (req.after) {
      groups.push(current)
      current = []
    }
  }
  if (current.length > 0) groups.push(current)
  return groups
}

type BuildStepsParams = {
  flowLabel: string
  shouldBatch: boolean
  signatureRequests: readonly SignatureRequest[]
  callRequests: readonly CallRequest[]
}

export function buildSteps({
  flowLabel,
  shouldBatch,
  signatureRequests,
  callRequests
}: BuildStepsParams): Step[] {
  let callSteps: Step[]
  if (shouldBatch) {
    const groups = groupCallRequests(callRequests)
    callSteps = groups.map((group): Step => {
      if (group.length === 1) {
        // Even a single-call group keeps `wallet_sendCalls` on EIP-5792 wallets
        // so Safe / multisig flows retain their proposal path. Modelling it as
        // a `batchTx` of length 1 preserves the before/after callback dispatch
        // path and the single send-calls request format.
        const only = group.at(0)
        if (!only) throw new Error('unreachable: group with length 1 has no element 0')
        const batch: BatchRequest = {
          label: only.label,
          callRequests: group,
          getCalls: [only.getCall]
        }
        return { type: 'batchTx', ...batch }
      }
      const batch: BatchRequest = {
        label: flowLabel,
        callRequests: group,
        getCalls: group.map(req => req.getCall)
      }
      return { type: 'batchTx', ...batch }
    })
  } else {
    callSteps = callRequests.map(req => ({ type: 'singleTx' as const, ...req }))
  }

  const _signatureRequests = signatureRequests.map(req => ({ type: 'signature' as const, ...req }))
  return [..._signatureRequests, ...callSteps]
}
